<template>
  <BaseListRow surface="grey" padding="none" hoverable class="tw-flex tw-flex-col">
    <div
      v-if="hasOnboardingPending"
      class="tw-bg-orange-50 tw-px-4 tw-py-2 tw-rounded-t"
    >
      <span class="tw-text-orange-500 tw-font-semibold tw-text-sm">
        {{ $t('Required client onboarding pending') }}
      </span>
    </div>
    <WdsStack align-center justify-space-between gap-xs class="tw-p-4 tw-w-full">
      <WdsStack gap-md direction-row>
        <a :href="laravelNamedRoute(namedRoutes.frontend.clients.index)">
          <WdsAvatar v-if="clientUnwrapped.company.avatar" :path="clientUnwrapped.company.avatar" />
          <WdsStack
            v-else
            class="tw-w-8 tw-h-8 tw-bg-indigo-50 tw-rounded"
            justify-center
            align-center
          >
            <WdsIcon :name="WdsIconNameEnum.ENTERPRISE" color="#5d36f1" :size="21" />
          </WdsStack>
        </a>
        <WdsStack direction-column>
          <WdsText v-if="clientUnwrapped.status === ContactStatus.Active" secondary>
            <WdsStack gap-sm>
              <WdsLabel success subtle sm>
                {{ $t('Active') }}
              </WdsLabel>
              <span>{{ $t('since :date', { date: clientUnwrapped.invitedAt }) }}</span>
            </WdsStack>
          </WdsText>
          <WdsText v-if="clientUnwrapped.status === ContactStatus.Invited" secondary>
            <WdsStack gap-sm>
              <WdsLabel primary subtle sm>
                {{ $t('New invitation') }}
              </WdsLabel>
              <span>{{ $t('sent on :date', { date: clientUnwrapped.invitedAt }) }}</span>
            </WdsStack>
          </WdsText>
          <WdsText v-if="clientUnwrapped.status === ContactStatus.Applied" secondary>
            <WdsStack gap-sm>
              <WdsLabel subtle sm>
                {{ $t('Awaiting approval') }}
              </WdsLabel>
              <span>{{ $t('applied on :date', { date: clientUnwrapped.invitedAt }) }}</span>
            </WdsStack>
          </WdsText>

          <WdsText :text="clientUnwrapped.company.name" class="tw-font-bold" />
        </WdsStack>
      </WdsStack>

      <WdsStack align-center gap-xs>
        <WdsButton
          v-if="clientUnwrapped.status === ContactStatus.Invited"
          primary
          :href="laravelNamedRoute(namedRoutes.frontend.clients.index)"
        >
          {{ $t('Accept') }}
        </WdsButton>
        <WdsMenu z-index="50" placement="bottom-end">
          <template #trigger>
            <WdsButtonGhost>
              <WdsIcon name="overflow-menu-vertical" :size="16" />
            </WdsButtonGhost>
          </template>

          <WdsMenuOption
            v-if="hasOnboardingPending"
            :href="laravelNamedRoute(namedRoutes.frontend.clients.index)"
          >
            <WdsIcon :size="16" :name="WdsIconNameEnum.EDIT" />
            {{ $t('Edit profile') }}
          </WdsMenuOption>

          <WdsMenuOption
            v-if="clientUnwrapped.token && clientUnwrapped.status === ContactStatus.Invited"
            :href="`contacts/decline/${clientUnwrapped.id}/${clientUnwrapped.token}`"
            theme="DESTRUCTIVE"
          >
            <WdsIcon :size="16" :name="WdsIconNameEnum.CLOSE_OUTLINE" />
            {{ $t('Decline invitation') }}
          </WdsMenuOption>

          <WdsTooltip
            v-if="
              !clientUnwrapped.canLeaveClient && clientUnwrapped.status === ContactStatus.Active
            "
            global
            :text="$t('You can’t leave while there is an active contract with the client')"
          >
            <WdsMenuOption
              v-if="clientUnwrapped.status === ContactStatus.Active"
              disabled
              theme="DESTRUCTIVE"
            >
              <WdsIcon :size="16" :name="WdsIconNameEnum.EXIT" />
              {{ $t('Leave talent pool') }}
            </WdsMenuOption>
          </WdsTooltip>
          <WdsMenuOption
            v-else-if="clientUnwrapped.status === ContactStatus.Active"
            theme="DESTRUCTIVE"
            class="swal-delete faded"
            :data-swaltext="$t('Do you want to leave this client for good?')"
            :data-swalconfirmtext="$t('Yes, leave')"
            :data-formid="`remove-client-${hashSum(clientUnwrapped.id)}`"
          >
            <WdsIcon :size="16" :name="WdsIconNameEnum.EXIT" />
            {{ $t('Leave talent pool') }}
          </WdsMenuOption>

          <WdsMenuOption
            v-if="clientUnwrapped.status === ContactStatus.Applied"
            class="swal-delete faded"
            :data-swaltext="$t('Are you sure you want to cancel this request?')"
            :data-swalconfirmtext="$t('Yes')"
            :data-formid="`remove-client-${hashSum(clientUnwrapped.id)}`"
            theme="DESTRUCTIVE"
          >
            <WdsIcon :size="16" :name="WdsIconNameEnum.CLOSE_OUTLINE" />
            {{ $t('Cancel request') }}
          </WdsMenuOption>
        </WdsMenu>
      </WdsStack>
      <teleport to="body">
        <BaseHtmlForm
          :id="`remove-client-${hashSum(clientUnwrapped.id)}`"
          method="delete"
          :action="laravelNamedRoute(namedRoutes.contacts.destroy, { id: clientUnwrapped.id })"
        >
          <input type="hidden" name="redirect_uri" :value="href" />
        </BaseHtmlForm>
      </teleport>
    </WdsStack>
  </BaseListRow>
</template>

<script setup lang="ts">
import {
  WdsAvatar,
  WdsButton,
  WdsButtonGhost,
  WdsIcon,
  WdsIconNameEnum,
  WdsLabel,
  WdsMenu,
  WdsMenuOption,
  WdsStack,
  WdsText,
  WdsTooltip,
} from '@worksome/wds-components'
import { computed } from 'vue'
import hashSum from 'hash-sum'
import { ContactStatus, TrustedContactOnboardingStatus, WorkerClientFragmentDoc } from '@/gql/graphql'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { FragmentType, graphql, useFragment } from '@/gql'
import { useTranslation } from '@/composables/translation'
import BaseHtmlForm from '@/components/BaseHtmlForm.vue'
import BaseListRow from '@/components/BaseListRow.vue'

const { $t } = useTranslation()

const workerClientFragment = graphql(`
  fragment WorkerClient on Client {
    id
    status
    invitedAt
    token
    origin
    originChannel
    requiresOnboarding
    onboardingStatus
    canLeaveClient
    company {
      id
      name
      avatar
    }
  }
`)

const props = defineProps<{ client: FragmentType<typeof WorkerClientFragmentDoc> }>()
const { href } = document.location
const clientUnwrapped = computed(() => useFragment(workerClientFragment, props.client))
const hasOnboardingPending = computed(
  () =>
    clientUnwrapped.value.requiresOnboarding &&
    clientUnwrapped.value.onboardingStatus !== null &&
    clientUnwrapped.value.onboardingStatus !== TrustedContactOnboardingStatus.Completed,
)
</script>

